import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { CellWithTwoValues } from '@tap/mui-ui';
import { PriorityFieldCell, SelectFieldCell, StatusFieldCell } from './custom-cells';
import { Link as MuiLink } from '@mui/material';
import { Link } from 'react-router';
import React, { ReactElement } from 'react';
import { userService, DocumentFieldType, IApiDocumentSchema, IDocumentSchemaSettings, DynamicGridColumnProps, DynamicGridColumnComponent, DynamicGridColumn } from '@tap/data-providers';
import { useDocuemtnSchema } from '../context';
import { GridColDef } from '@mui/x-data-grid-pro';
import { dateTimeHelper } from '@tap/utils';

export const RenderDynamicField = ({
    documentSettings, columnProps, rowField, row,
}: {
    documentSettings?: IDocumentSchemaSettings,
    columnProps?: DynamicGridColumnProps,
    rowField: string,
    row: any,
}): ReactElement<any, any> => {
    const fieldProps = getFieldProps(rowField, columnProps, documentSettings);

    const fieldValue = fieldProps.sourceObject
        ? row[fieldProps.sourceObject][rowField]
        : row[rowField];

    if (fieldProps.componentType === DocumentFieldType.DocumentKey) {
        return (
            <MuiLink
                component={Link}
                to={`/${fieldValue}`}
            >
                {fieldValue}
            </MuiLink>
        );
    }


    if (fieldProps.componentType === DocumentFieldType.UtcDate) {
        const defaultDateFormat = 'dd.MM.yyyy HH:mm:ss';

        const utcDateProps = fieldProps.typeProps;

        return <>{dateTimeHelper.format(fieldValue, utcDateProps.displayFormat || defaultDateFormat)}</>;
    }

    if (fieldProps.componentType === DocumentFieldType.String || fieldProps.componentType === DocumentFieldType.Number) {
        return <>{fieldValue}</>;
    }


    if (fieldProps.componentType === DocumentFieldType.Priority) {
        return <PriorityFieldCell value={fieldValue} />;
    }

    if (fieldProps.componentType === DocumentFieldType.User) {
        const value = userService.getUserFIO(fieldValue);
        return (
            <div>
                {value}
            </div>
        )
    }

    if (fieldProps.componentType === DocumentFieldType.Status) {
        const sourceList = documentSettings.statusList;
        const selectedValue = sourceList.find(p => p.code === fieldValue);

        return <StatusFieldCell value={selectedValue} />;
    }

    if (fieldProps.componentType === DocumentFieldType.Identifier) {
        const sourceList = documentSettings[fieldProps.typeProps.sourceListPropertyKey];
        const selectedValue = sourceList.find(p => p.code === fieldValue);
        return <SelectFieldCell value={selectedValue} />;
    }

    return <>{(fieldValue || '').toString() || '-'}</>;
}


export const DynamicDocumentCell = (
    cellParams: GridRenderCellParams<any, any, any>,
): ReactElement<any, any> => {
    const columnProps: DynamicGridColumnProps = cellParams.colDef;
    const row = cellParams.row;
    const documentCode = row.documentCode;
    const documentSchema = documentCode ? useDocuemtnSchema(documentCode) : undefined;
    const documentSettings = documentSchema?.settings;


    if (columnProps.componentName === DynamicGridColumnComponent.TwoLineCell) {

        const renderProps = Object.keys(columnProps.sourcePropertyMap)
            .reduce((acc, val) => {
                acc[val] = RenderDynamicField({ documentSettings, row, rowField: columnProps.sourcePropertyMap[val] })
                return acc;
            }, {} as any);


        return <CellWithTwoValues
            {...renderProps}
            {...columnProps.componentProps}
            emptySecondaryValueCharacter={null}
        />;
    }

    const rowField = cellParams.colDef.sourceProperty;

    return RenderDynamicField({ documentSettings, columnProps, rowField, row });
}

export const getFieldProps = (propertyKey: string, columnProps?: DynamicGridColumnProps, documentSettings?: IDocumentSchemaSettings) => {
    const fieldProps = documentSettings?.dataProperties[propertyKey] || {};

    return {
        propertyKey,
        componentType: columnProps?.componentName || fieldProps?.type || DocumentFieldType.String,
        typeProps: fieldProps?.typeProps,
    };
}


export const getDocumentGridColumnList = (columns: DynamicGridColumn[], schemaProps: IApiDocumentSchema): GridColDef<any, any, any>[] => {

    return columns
        .map((col, idx) => {
            let columnProps = typeof col === 'string'
                ? { sourceProperty: col }
                : col;



            if (schemaProps && columnProps.sourceProperty) {
                const fieldProps = schemaProps.settings.dataProperties[columnProps.sourceProperty];

                if (!fieldProps) {
                    return {
                        field: 'hide',
                        hide: true,
                    };
                }

                columnProps = {
                    headerName: fieldProps.name,
                    ...fieldProps.columnProps,
                    ...columnProps,
                };
            }

            return {
                field: `column_${idx}`,
                sortable: false,
                filterable: false,
                ...columnProps,
                renderCell: DynamicDocumentCell,
            };
        })
        .filter((col) => !col.hide);
};